import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Building2, MapPin, User, Upload, FileText, CheckCircle } from "lucide-react";
import { COUNTRIES, getCountiesForCountry } from "../constants/locations";

const ClubApplicationForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Club Info
    club_name: "",
    club_type: "",
    year_established: "",
    number_of_members: "",
    affiliated_body: "",
    registration_number: "",
    website: "",
    facebook: "",
    twitter: "",
    instagram: "",
    linkedin: "",
    
    // Address
    country: "",
    region: "",
    city: "",
    address: "",
    postal_code: "",
    
    // Contact Person
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    
    // Banking
    bank_country: "",
    account_holder_name: "",
    
    // India CSR Compliance (conditional - only if country = India)
    india_legal_entity_type: "",
    india_pan_number: "",
    india_csr_registration: "",
    india_ngo_darpan_id: "",
    india_years_track_record: "",
    india_csr_activity_focus: "",
    india_project_type: "",
    india_admin_overheads: ""
  });

  const [regions, setRegions] = useState([]);
  const [showRegionInput, setShowRegionInput] = useState(false);

  // Files
  const [affiliationDoc, setAffiliationDoc] = useState(null);
  const [bankDoc, setBankDoc] = useState(null);
  
  // India CSR Documents
  const [indiaIncorporationCert, setIndiaIncorporationCert] = useState(null);
  const [india12ACert, setIndia12ACert] = useState(null);
  const [india80GCert, setIndia80GCert] = useState(null);
  const [indiaFinancialStatements, setIndiaFinancialStatements] = useState(null);

  const handleCountryChange = (country) => {
    setFormData({ ...formData, country, region: "" });
    const countryRegions = getCountiesForCountry(country);
    
    if (countryRegions && countryRegions.length > 0) {
      setRegions(countryRegions);
      setShowRegionInput(false);
    } else {
      setRegions([]);
      setShowRegionInput(true);
    }
  };

  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    
    if (file.size > maxSize) {
      toast.error("File must be less than 5MB");
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PDF, JPG, and PNG files are allowed");
      return false;
    }
    
    return true;
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      if (type === 'affiliation') {
        setAffiliationDoc(file);
      } else {
        setBankDoc(file);
      }
    } else {
      e.target.value = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!affiliationDoc || !bankDoc) {
      toast.error("Please upload both required documents");
      return;
    }
    
    // India CSR validation (warnings only, not blocking)
    if (formData.country === "India") {
      const missingFields = [];
      if (!formData.india_legal_entity_type) missingFields.push("Legal Entity Type");
      if (!formData.india_pan_number) missingFields.push("PAN Number");
      if (!formData.india_csr_registration) missingFields.push("CSR Registration");
      if (!formData.india_years_track_record) missingFields.push("Years of Track Record");
      if (!formData.india_csr_activity_focus) missingFields.push("CSR Activity Focus");
      if (!formData.india_project_type) missingFields.push("Project Type");
      if (!indiaIncorporationCert) missingFields.push("Incorporation Certificate");
      if (!india12ACert) missingFields.push("12A Certificate");
      if (!india80GCert) missingFields.push("80G Certificate");
      if (!indiaFinancialStatements) missingFields.push("Financial Statements");
      
      if (missingFields.length > 0) {
        toast.warning(`CSR Compliance incomplete: ${missingFields.join(", ")}. Application will be submitted but may be delayed.`, {
          duration: 5000
        });
      }
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Add files
      submitData.append('affiliation_document', affiliationDoc);
      submitData.append('bank_document', bankDoc);
      
      // Add India CSR documents if uploaded
      if (indiaIncorporationCert) {
        submitData.append('india_incorporation_cert', indiaIncorporationCert);
      }
      if (india12ACert) {
        submitData.append('india_12a_cert', india12ACert);
      }
      if (india80GCert) {
        submitData.append('india_80g_cert', india80GCert);
      }
      if (indiaFinancialStatements) {
        submitData.append('india_financial_statements', indiaFinancialStatements);
      }

      const response = await axios.post(`${API}/club-applications`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitted(true);
      toast.success("Application submitted successfully!");
      
    } catch (error) {
      console.error("Application error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">Application Submitted!</CardTitle>
            <CardDescription className="text-base mt-4">
              Thank you for applying to join PlayBack Token. We have received your application and will review it shortly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              You will receive an email at <strong>{formData.email}</strong> with updates on your application status.
            </p>
            <p className="text-sm text-gray-600">
              Our team typically reviews applications within 3-5 business days.
            </p>
            <Button onClick={() => navigate("/")} className="w-full">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-6">
          ← Back to Home
        </Link>

        <Card>
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" 
              alt="PlayBack Token" 
              className="w-24 h-24 mx-auto mb-4 object-contain"
            />
            <CardTitle className="text-3xl font-bold text-red-600">Club Application</CardTitle>
            <CardDescription className="text-base">
              Apply to join PlayBack Token and monetize your volunteer hours
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Club Information */}
              <div className="space-y-4 p-4 bg-red-50 rounded-lg">
                <h3 className="font-semibold text-red-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Club Information
                </h3>
                
                <div>
                  <Label htmlFor="club_name">Club Name *</Label>
                  <Input
                    id="club_name"
                    required
                    value={formData.club_name}
                    onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                    placeholder="e.g., Kells GAA Club"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="club_type">Organisation Type *</Label>
                    <Select value={formData.club_type} onValueChange={(value) => setFormData({ ...formData, club_type: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sports">Sports Club</SelectItem>
                        <SelectItem value="community">Community Group</SelectItem>
                        <SelectItem value="charity">Charity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="year_established">Year Established *</Label>
                    <Input
                      id="year_established"
                      type="number"
                      required
                      value={formData.year_established}
                      onChange={(e) => setFormData({ ...formData, year_established: e.target.value })}
                      placeholder="e.g., 2010"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="number_of_members">Number of Members *</Label>
                  <Select value={formData.number_of_members} onValueChange={(value) => setFormData({ ...formData, number_of_members: value })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select member range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10</SelectItem>
                      <SelectItem value="11-25">11-25</SelectItem>
                      <SelectItem value="26-50">26-50</SelectItem>
                      <SelectItem value="51-100">51-100</SelectItem>
                      <SelectItem value="101-250">101-250</SelectItem>
                      <SelectItem value="251-500">251-500</SelectItem>
                      <SelectItem value="500+">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="affiliated_body">Affiliated Body/Organization *</Label>
                  <Input
                    id="affiliated_body"
                    required
                    value={formData.affiliated_body}
                    onChange={(e) => setFormData({ ...formData, affiliated_body: e.target.value })}
                    placeholder="e.g., GAA, FAI, Charity Commission"
                  />
                </div>

                <div>
                  <Label htmlFor="registration_number">Registration/Charity Number</Label>
                  <Input
                    id="registration_number"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder="e.g., CHY12345 (if applicable)"
                  />
                </div>

                <div>
                  <Label htmlFor="website">Club Website</Label>
                  <Input
                    id="website"
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.yourclub.com"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="mb-3 block">Social Media (Optional)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Facebook URL"
                      value={formData.facebook}
                      onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    />
                    <Input
                      placeholder="Twitter/X URL"
                      value={formData.twitter}
                      onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    />
                    <Input
                      placeholder="Instagram URL"
                      value={formData.instagram}
                      onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    />
                    <Input
                      placeholder="LinkedIn URL"
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Club Address
                </h3>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Select value={formData.country} onValueChange={handleCountryChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.value} value={country.value}>{country.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="region">
                    {formData.country === "United States" ? "State" : 
                     formData.country === "Australia" ? "State/Territory" :
                     formData.country === "Canada" ? "Province" :
                     "County/Region"} *
                  </Label>
                  {showRegionInput ? (
                    <Input
                      id="region"
                      required
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      placeholder="Enter region"
                    />
                  ) : (
                    <Select value={formData.region} onValueChange={(val) => setFormData({ ...formData, region: val })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map(region => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div>
                  <Label htmlFor="city">City/Town *</Label>
                  <Input
                    id="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Kells"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Input
                    id="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street address"
                  />
                </div>

                <div>
                  <Label htmlFor="postal_code">Postal/ZIP Code *</Label>
                  <Input
                    id="postal_code"
                    required
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="e.g., A82 XY12"
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-green-900 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Contact Person
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john.smith@club.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+353 86 123 4567"
                  />
                </div>

                <div>
                  <Label htmlFor="position">Position in Club *</Label>
                  <Input
                    id="position"
                    required
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="e.g., Chairperson, Secretary, Treasurer"
                  />
                </div>
              </div>

              {/* Document Uploads */}
              <div className="space-y-4 p-4 bg-yellow-50 rounded-lg">
                <h3 className="font-semibold text-yellow-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Required Documents
                </h3>

                <div>
                  <Label htmlFor="affiliation_doc">Proof of Affiliation *</Label>
                  <div className="mt-2">
                    <label htmlFor="affiliation_doc" className="flex items-center justify-center w-full h-32 px-4 border-2 border-dashed border-yellow-300 rounded-lg cursor-pointer bg-white hover:bg-yellow-50">
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                        <p className="text-sm text-gray-600">
                          {affiliationDoc ? affiliationDoc.name : "Click to upload"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, JPG, PNG (Max 5MB)
                        </p>
                      </div>
                    </label>
                    <input
                      id="affiliation_doc"
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'affiliation')}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Certificate from sporting body, charity registration, or official letter
                  </p>
                </div>

                <div>
                  <Label htmlFor="bank_doc">Bank Account Proof *</Label>
                  <div className="mt-2">
                    <label htmlFor="bank_doc" className="flex items-center justify-center w-full h-32 px-4 border-2 border-dashed border-yellow-300 rounded-lg cursor-pointer bg-white hover:bg-yellow-50">
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                        <p className="text-sm text-gray-600">
                          {bankDoc ? bankDoc.name : "Click to upload"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, JPG, PNG (Max 5MB)
                        </p>
                      </div>
                    </label>
                    <input
                      id="bank_doc"
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'bank')}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Bank statement, bank letter, or account confirmation (must show account holder name and number)
                  </p>
                </div>
              </div>

              {/* Banking Info */}
              <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                <h3 className="font-semibold text-purple-900">Banking Information</h3>
                
                <div>
                  <Label htmlFor="bank_country">Bank Country *</Label>
                  <Select value={formData.bank_country} onValueChange={(val) => setFormData({ ...formData, bank_country: val })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.value} value={country.value}>{country.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="account_holder_name">Account Holder Name *</Label>
                  <Input
                    id="account_holder_name"
                    required
                    value={formData.account_holder_name}
                    onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                    placeholder="Must match uploaded bank document"
                  />
                </div>

                <p className="text-xs text-gray-600 bg-white p-3 rounded">
                  ℹ️ Full bank details will be securely collected after approval via Stripe Connect
                </p>
              </div>

              {/* India CSR Compliance Section - Conditional */}
              {formData.country === "India" && (
                <div className="space-y-4 p-6 bg-gradient-to-br from-orange-50 to-green-50 rounded-lg border-2 border-orange-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-orange-900 text-lg">India CSR Compliance</h3>
                      <p className="text-sm text-orange-700">Required for Indian Organizations under Companies Act, 2013</p>
                    </div>
                  </div>

                  {/* 1. Statutory Registration Details */}
                  <div className="space-y-4 p-4 bg-white rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Statutory Registration Details
                    </h4>
                    
                    <div>
                      <Label htmlFor="india_legal_entity_type">Legal Entity Type (for CSR) *</Label>
                      <Select 
                        value={formData.india_legal_entity_type} 
                        onValueChange={(val) => setFormData({ ...formData, india_legal_entity_type: val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select legal entity type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Registered Public Trust">Registered Public Trust</SelectItem>
                          <SelectItem value="Registered Society">Registered Society</SelectItem>
                          <SelectItem value="Section 8 Company">Section 8 Company</SelectItem>
                        </SelectContent>
                      </Select>
                      {!formData.india_legal_entity_type && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ Required for CSR compliance</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="india_pan_number">PAN of the Entity *</Label>
                      <Input
                        id="india_pan_number"
                        value={formData.india_pan_number}
                        onChange={(e) => setFormData({ ...formData, india_pan_number: e.target.value.toUpperCase() })}
                        placeholder="AAAPL1234C"
                        maxLength={10}
                      />
                      {formData.india_pan_number && formData.india_pan_number.length !== 10 && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ PAN must be exactly 10 characters</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="india_csr_registration">CSR Registration Number (Form CSR-1) *</Label>
                      <Input
                        id="india_csr_registration"
                        value={formData.india_csr_registration}
                        onChange={(e) => setFormData({ ...formData, india_csr_registration: e.target.value })}
                        placeholder="18-digit MCA registration number"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Must be registered on the Ministry of Corporate Affairs (MCA) portal
                      </p>
                      {formData.india_csr_registration && formData.india_csr_registration.length !== 18 && (
                        <p className="text-xs text-orange-600">⚠️ CSR Registration should be 18 digits</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="india_ngo_darpan_id">NGO Darpan ID (Optional but Recommended)</Label>
                      <Input
                        id="india_ngo_darpan_id"
                        value={formData.india_ngo_darpan_id}
                        onChange={(e) => setFormData({ ...formData, india_ngo_darpan_id: e.target.value })}
                        placeholder="NITI Aayog portal ID"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Highly recommended for funding eligibility
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="india_years_track_record">Years of Established Track Record *</Label>
                      <Input
                        id="india_years_track_record"
                        type="number"
                        min="0"
                        value={formData.india_years_track_record}
                        onChange={(e) => setFormData({ ...formData, india_years_track_record: e.target.value })}
                        placeholder="Number of years active"
                      />
                      {formData.india_years_track_record && parseInt(formData.india_years_track_record) < 3 && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ Most private entities require 3+ years of track record</p>
                      )}
                    </div>
                  </div>

                  {/* 2. Project Eligibility & Financials */}
                  <div className="space-y-4 p-4 bg-white rounded-lg border border-green-200">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Project Eligibility & Financials
                    </h4>

                    <div>
                      <Label htmlFor="india_csr_activity_focus">Primary CSR Activity Focus *</Label>
                      <Select 
                        value={formData.india_csr_activity_focus} 
                        onValueChange={(val) => setFormData({ ...formData, india_csr_activity_focus: val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select primary activity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Promoting Education">Promoting Education</SelectItem>
                          <SelectItem value="Health & Sanitation">Health & Sanitation</SelectItem>
                          <SelectItem value="Environmental Sustainability">Environmental Sustainability</SelectItem>
                          <SelectItem value="Eradicating Poverty">Eradicating Poverty</SelectItem>
                          <SelectItem value="Training for Sports">Training for Sports</SelectItem>
                          <SelectItem value="Protection of Heritage">Protection of Heritage</SelectItem>
                          <SelectItem value="Other Schedule VII Activity">Other Schedule VII Activity</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-600 mt-1">
                        Must fall under Schedule VII categories
                      </p>
                    </div>

                    <div>
                      <Label>Project Type Declaration *</Label>
                      <div className="space-y-2 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="india_project_type"
                            value="Ongoing Project (multi-year)"
                            checked={formData.india_project_type === "Ongoing Project (multi-year)"}
                            onChange={(e) => setFormData({ ...formData, india_project_type: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Ongoing Project (multi-year)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="india_project_type"
                            value="One-Time Project"
                            checked={formData.india_project_type === "One-Time Project"}
                            onChange={(e) => setFormData({ ...formData, india_project_type: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">One-Time Project</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="india_admin_overheads">Estimated Administrative Overheads (in INR)</Label>
                      <Input
                        id="india_admin_overheads"
                        type="number"
                        min="0"
                        value={formData.india_admin_overheads}
                        onChange={(e) => setFormData({ ...formData, india_admin_overheads: e.target.value })}
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        ℹ️ Note: Must not exceed 5% of total CSR spend per regulations
                      </p>
                    </div>
                  </div>

                  {/* 3. Required Documents */}
                  <div className="space-y-4 p-4 bg-white rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Required Documents (Mandatory Uploads)
                    </h4>
                    <p className="text-xs text-gray-600">
                      All documents must be in PDF format, max 10MB each
                    </p>

                    <div>
                      <Label htmlFor="india_incorporation_cert">Certificate of Incorporation/Registration *</Label>
                      <Input
                        id="india_incorporation_cert"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
                            setIndiaIncorporationCert(file);
                          } else {
                            toast.error("Please upload a PDF file under 10MB");
                            e.target.value = '';
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Trust Deed, Society Registration Certificate, or Section 8 COI
                      </p>
                      {indiaIncorporationCert && (
                        <p className="text-xs text-green-600 mt-1">✓ {indiaIncorporationCert.name}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="india_12a_cert">12A Certificate *</Label>
                      <Input
                        id="india_12a_cert"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
                            setIndia12ACert(file);
                          } else {
                            toast.error("Please upload a PDF file under 10MB");
                            e.target.value = '';
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Proof of tax exemption under Section 12A of Income Tax Act
                      </p>
                      {india12ACert && (
                        <p className="text-xs text-green-600 mt-1">✓ {india12ACert.name}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="india_80g_cert">80G Certificate *</Label>
                      <Input
                        id="india_80g_cert"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
                            setIndia80GCert(file);
                          } else {
                            toast.error("Please upload a PDF file under 10MB");
                            e.target.value = '';
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Proof of tax deduction for donors under Section 80G (Highly preferred by funding companies)
                      </p>
                      {india80GCert && (
                        <p className="text-xs text-green-600 mt-1">✓ {india80GCert.name}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="india_financial_statements">Last 3 Years Audited Financial Statements *</Label>
                      <Input
                        id="india_financial_statements"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
                            setIndiaFinancialStatements(file);
                          } else {
                            toast.error("Please upload a PDF file under 10MB");
                            e.target.value = '';
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Please consolidate all 3 years into one PDF document. Proves financial stability and 3-year track record.
                      </p>
                      {indiaFinancialStatements && (
                        <p className="text-xs text-green-600 mt-1">✓ {indiaFinancialStatements.name}</p>
                      )}
                    </div>
                  </div>

                  {/* Warning if incomplete */}
                  {(!formData.india_legal_entity_type || 
                    !formData.india_pan_number || 
                    !formData.india_csr_registration || 
                    !formData.india_years_track_record ||
                    !formData.india_csr_activity_focus ||
                    !formData.india_project_type ||
                    !indiaIncorporationCert ||
                    !india12ACert ||
                    !india80GCert ||
                    !indiaFinancialStatements) && (
                    <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <p className="text-sm text-yellow-800 font-semibold">
                        ⚠️ CSR Compliance Incomplete
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        You can submit the application, but incomplete CSR documentation may delay approval. 
                        Please ensure all required fields and documents are provided for faster processing.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6"
                >
                  {loading ? "Submitting Application..." : "Submit Application"}
                </Button>

                <p className="text-xs text-center text-gray-600">
                  By submitting this application, you agree to our Terms of Service and Privacy Policy.
                  Applications are typically reviewed within 3-5 business days.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClubApplicationForm;
